import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  MessagesSquare,
  Video,
  Search,
  Puzzle,
} from "lucide-react";
import "./CH20041.scss";
// ============================================================================
// DATA
// ============================================================================

type Cycle = "monthly" | "annual";

interface Tier {
  key: string;
  name: string;
  blurb: string;
  monthly: number;
  annual: number;
  cta: string;
  highlighted?: boolean;
  features: string[];
}

const TIERS: Tier[] = [
  {
    key: "free",
    name: "Free",
    blurb: "For small teams trying it out",
    monthly: 0,
    annual: 0,
    cta: "Start for free",
    features: [
      "Unlimited members",
      "90-day message history",
      "10 integrations",
      "1:1 and group huddles",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    blurb: "For growing teams who live in chat all day",
    monthly: 8,
    annual: 6,
    cta: "Start free trial",
    highlighted: true,
    features: [
      "Unlimited message history",
      "Unlimited integrations",
      "Group video huddles & screen share",
      "Workflow builder",
      "Guest accounts for external partners",
      "Priority support",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    blurb: "For organizations with compliance and scale needs",
    monthly: 0,
    annual: 0,
    cta: "Talk to sales",
    features: [
      "SSO & SCIM provisioning",
      "Org-wide retention & legal hold",
      "Advanced admin & audit logs",
      "Dedicated success manager",
      "99.99% uptime SLA",
      "Custom data residency",
    ],
  },
];

const FEATURES = [
  {
    icon: MessagesSquare,
    photo: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80&auto=format&fit=crop",
    title: "Channels that keep work organized, not buried",
    body: "Conversations live in channels instead of inboxes, with threads keeping side discussions out of the main flow so nothing important gets lost in the scroll.",
  },
  {
    icon: Video,
    photo: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&q=80&auto=format&fit=crop",
    title: "Huddles for the conversations that need a voice",
    body: "Jump into a lightweight audio or video huddle straight from any channel or DM, no link to send, no meeting to schedule — just click and talk.",
  },
  {
    icon: Search,
    photo: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&q=80&auto=format&fit=crop",
    title: "Search that actually finds what you need",
    body: "Every message, file, and decision is searchable the moment it's sent, so the answer to 'didn't we discuss this already?' is one query away, not a scroll.",
  },
];

const STORIES = [
  {
    company: "Marlowe & Voss",
    industry: "Recruiting SaaS",
    metric: "Internal email down 40%",
    quote:
      "We didn't realize how much time we lost to CC chains until we moved every project into channels. New hires are productive in days instead of weeks now.",
    person: "VP of Operations",
  },
  {
    company: "Aldergate Partners",
    industry: "Consulting",
    metric: "5 fewer standups per week",
    quote:
      "Huddles replaced most of our recurring status calls. People jump in for ten minutes instead of blocking thirty on a calendar that nobody wanted to attend.",
    person: "Engineering Manager",
  },
  {
    company: "Brightwell Clinic Group",
    industry: "Healthcare",
    metric: "12 locations, one shared workspace",
    quote:
      "Twelve clinics used to mean twelve group texts and a lot of missed information. Now everything's in channels, searchable, and nothing depends on one person's phone.",
    person: "Practice Manager",
  },
];

const FAQS = [
  {
    q: "Can we migrate our existing message history?",
    a: "Yes. Guided import tools bring over channels, message history, and files from most major chat platforms, so your team doesn't lose context when switching.",
  },
  {
    q: "Does it integrate with the tools we already use?",
    a: "Yes. The platform connects with calendars, file storage, project trackers, and CI/CD tools out of the box, plus a workflow builder for custom automations.",
  },
  {
    q: "Can we add external partners or clients?",
    a: "Pro and Enterprise plans support guest accounts, so external collaborators can join specific channels without getting access to your whole workspace.",
  },
  {
    q: "Is our message data secure?",
    a: "Data is encrypted in transit and at rest, with SOC 2 Type II compliance. Enterprise plans add SSO/SCIM, org-wide retention policies, and audit logs.",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const CH20041: React.FC = () => {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Every CTA on this page routes here.
  const goToSignup = () => navigate("/auth/signup");

  return (
    <div className="wrap">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">Team Chat</span>
            <h1>Where your team actually gets work done, together</h1>
            <p className="hero-sub">
              Channels, huddles, and search that doesn't make you scroll —
              built for teams who are tired of decisions getting lost across
              five different inboxes and a dozen group chats.
            </p>
            <div className="hero-cta">
              <button className="btn-primary" onClick={goToSignup}>
                Start for free <ArrowRight size={16} />
              </button>
              <button className="btn-ghost" onClick={goToSignup}>Book a demo</button>
            </div>
            <p className="hero-fineprint">
              Free for unlimited members &nbsp;·&nbsp; No credit card required
            </p>
          </div>

          <div className="hero-art">
            <img
              className="hero-photo"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80&auto=format&fit=crop"
              alt="Team collaborating around a laptop"
            />
            <div className="hero-photo-card">
              <span className="hero-photo-card-label">Active now</span>
              <span className="hero-photo-card-value">214</span>
              <span className="hero-photo-card-sub">across 18 channels</span>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO BAR */}
      <section className="logobar">
        <span className="logobar-label">Trusted by teams at</span>
        <div className="logobar-strip">
          {[
            { name: "Marlowe & Voss", mark: "MV" },
            { name: "Aldergate Partners", mark: "AP" },
            { name: "Brightwell Clinic", mark: "BC" },
            { name: "Harrow & Vance", mark: "HV" },
            { name: "Solene Beauty", mark: "SB" },
          ].map((c) => (
            <div className="logobar-item" key={c.name}>
              <span className="logobar-mark">{c.mark}</span>
              <span className="logobar-name">{c.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Platform</span>
          <h2>Everything a team needs to stay in sync, without the noise</h2>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div className="feature-card" key={f.title}>
              <div className="feature-photo-wrap">
                <img className="feature-photo" src={f.photo} alt="" />
                <span className="feature-icon">
                  <f.icon size={18} strokeWidth={1.75} />
                </span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing">
        <div className="section-head">
          <span className="eyebrow">Pricing</span>
          <h2>Free to start, simple to scale</h2>
          <p className="section-sub">
            All plans include unlimited channels, huddles, and core integrations.
          </p>
          <div className="toggle">
            <button
              className={cycle === "monthly" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={cycle === "annual" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("annual")}
            >
              Annual <span className="toggle-save">Save 25%</span>
            </button>
          </div>
        </div>

        <div className="pricing-grid">
          {TIERS.map((tier) => (
            <div
              className={tier.highlighted ? "tier tier-highlight" : "tier"}
              key={tier.key}
            >
              {tier.highlighted && <span className="tier-badge">Most popular</span>}
              <h3>{tier.name}</h3>
              <p className="tier-blurb">{tier.blurb}</p>
              <div className="tier-price">
                {tier.key === "enterprise" ? (
                  <span className="price-custom">Custom pricing</span>
                ) : tier.monthly === 0 ? (
                  <span className="price-custom">$0</span>
                ) : (
                  <>
                    <span className="price-amount">
                      ${cycle === "monthly" ? tier.monthly : tier.annual}
                    </span>
                    <span className="price-period">/ user / mo</span>
                  </>
                )}
              </div>
              <button
                className={tier.highlighted ? "btn-primary full" : "btn-secondary full"}
                onClick={goToSignup}
              >
                {tier.cta}
              </button>
              <ul className="tier-features">
                {tier.features.map((f) => (
                  <li key={f}>
                    <Check size={15} strokeWidth={2.25} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CLIENT SUCCESS STORIES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Customer stories</span>
          <h2>Results from teams running on the platform</h2>
        </div>
        <div className="story-grid">
          {STORIES.map((s) => (
            <div className="story-card" key={s.company}>
              <div className="story-metric">
                <TrendingUp size={16} strokeWidth={2} />
                {s.metric}
              </div>
              <p className="story-quote">&ldquo;{s.quote}&rdquo;</p>
              <div className="story-attrib">
                <div className="story-company">{s.company}</div>
                <div className="story-role">
                  {s.person} &middot; {s.industry}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY STRIP */}
      <section className="security">
        <div className="security-item">
          <ShieldCheck size={18} strokeWidth={1.75} />
          SOC 2 Type II compliant
        </div>
        <div className="security-item">
          <Puzzle size={18} strokeWidth={1.75} />
          Unlimited integrations
        </div>
        <div className="security-item">
          <Video size={18} strokeWidth={1.75} />
          99.99% uptime SLA
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section">
        <div className="section-head">
          <span className="eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <div className="faq-item" key={item.q}>
              <button
                className="faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <ChevronDown
                  size={18}
                  className={openFaq === i ? "faq-chev open" : "faq-chev"}
                />
              </button>
              {openFaq === i && <p className="faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-band">
        <h2>Get your team off email today</h2>
        <p>Free for unlimited members. No credit card required.</p>
        <div className="hero-cta center">
          <button className="btn-primary" onClick={goToSignup}>
            Start for free <ArrowRight size={16} />
          </button>
          <button className="btn-ghost on-dark" onClick={goToSignup}>Book a demo</button>
        </div>
      </section>
    </div>
  );
};

export default CH20041;